import { useEffect, useRef, useContext } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { getQuotaUsage } from '@/lib/supabase';

// Track which alerts have been shown to avoid spam
const shownAlerts = new Map<string, boolean>();

export const useQuota = () => {
  // Safely get auth context - handle case where AuthProvider isn't ready yet (HMR, etc.)
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;
  const hasAuthContext = authContext !== undefined;
  const userId = user?.id ?? null;
  const { toast } = useToast();
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckRef = useRef<{ current: number; limit: number | null } | null>(null);

  useEffect(() => {
    if (!hasAuthContext) {
      console.debug('useQuota: AuthProvider not ready, skipping quota check');
      return;
    }

    if (!userId) {
      // Clear any existing interval
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    const checkQuota = async () => {
      try {
        const quota = await getQuotaUsage();
        
        // Skip if unlimited
        if (quota.limit === null) {
          return;
        }

        // Only show alerts if quota has changed
        if (lastCheckRef.current) {
          const { current: lastCurrent, limit: lastLimit } = lastCheckRef.current;
          if (lastCurrent === quota.current && lastLimit === quota.limit) {
            return; // No change, skip alert
          }
        }

        lastCheckRef.current = { current: quota.current, limit: quota.limit };

        const percentage = quota.percentage;

        // Show alert at 100% (only once)
        if (quota.current >= quota.limit && !shownAlerts.get(`${userId}-100`)) {
          shownAlerts.set(`${userId}-100`, true);
          toast({
            title: "Property Limit Reached",
            description: `You've used all ${quota.limit} properties this month. Upgrade your plan or wait until next month.`,
            variant: "destructive",
            duration: 5000,
          });
        }
        // Show alert at 80% (only once)
        else if (percentage >= 80 && percentage < 100 && !shownAlerts.get(`${userId}-80`)) {
          const remaining = quota.limit - quota.current;
          if (remaining > 0) {
            shownAlerts.set(`${userId}-80`, true);
            toast({
              title: "Approaching Property Limit",
              description: `You have ${remaining} property${remaining === 1 ? '' : 's'} remaining this month. Consider upgrading your plan.`,
              variant: "default",
              duration: 5000,
            });
          }
        }
      } catch (error) {
        console.error('Error checking quota:', error);
      }
    };

    // Check immediately
    checkQuota();

    // Check every 60 seconds
    checkIntervalRef.current = setInterval(checkQuota, 60000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [hasAuthContext, userId, toast]);

  // Clear alerts when user changes
  useEffect(() => {
    return () => {
      if (userId) {
        // Clear alerts for this user when component unmounts
        const keysToDelete: string[] = [];
        shownAlerts.forEach((_, key) => {
          if (key.startsWith(`${userId}-`)) {
            keysToDelete.push(key);
          }
        });
        keysToDelete.forEach(key => shownAlerts.delete(key));
      }
    };
  }, [userId]);
};
